import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { http } from "@/lib/api";
import { renderBlock } from "@/components/farmer_site/BlockRenderer";
import { FarmerSiteHeader, FarmerSiteFooter } from "@/components/farmer_site/FarmerSiteChrome";
import { themeVars } from "@/lib/farmer_site_themes";
import { Plant, ArrowLeft } from "@phosphor-icons/react";

/**
 * FarmerSitePublic — /f/:slug
 *
 * Fetches the fully-resolved farm site from the public endpoint and
 * renders it through the shared block renderer. The theme's CSS
 * tokens are applied at the wrapper so the entire subtree gets the
 * chosen palette + fonts without any component-level knowledge.
 */
export default function FarmerSitePublic() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let alive = true;
    setState({ loading: true, error: null, data: null });
    http.get(`/farmers/websites/slug/${encodeURIComponent(slug)}`)
      .then(({ data }) => alive && setState({ loading: false, error: null, data }))
      .catch((e) => alive && setState({
        loading: false,
        error: e?.response?.status === 404 ? "not-found" : "error",
        data: null,
      }));
    return () => { alive = false; };
  }, [slug]);

  const themeStyle = useMemo(() => {
    if (!state.data?.website) return {};
    return themeVars(state.data.website.theme_id, state.data.website.theme_overrides);
  }, [state.data]);

  useEffect(() => {
    if (state.data?.website?.farm_name) {
      document.title = `${state.data.website.farm_name} · Farmers Pulse`;
    }
  }, [state.data]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50"
        data-testid="fs-public-loading">
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <Plant size={16} weight="fill" className="animate-pulse" /> Loading farm site…
        </div>
      </div>
    );
  }

  if (state.error === "not-found") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-neutral-50 px-5" data-testid="fs-public-not-found">
        <Plant size={40} weight="duotone" className="text-neutral-400" />
        <h1 className="mt-4 text-3xl font-serif">This farm hasn't opened its doors yet</h1>
        <p className="mt-2 text-neutral-500 text-center max-w-md">
          The farm you're looking for isn't published, or the address is different.
        </p>
        <Link to="/farmers" className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-emerald-700 text-white px-4 py-2 text-sm">
          <ArrowLeft size={12} weight="bold" /> Back to Farmers Pulse
        </Link>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-neutral-50 px-5">
        <p className="text-neutral-700">Something went wrong while loading this farm site.</p>
        <button onClick={() => window.location.reload()}
          className="mt-4 rounded-full bg-neutral-900 text-white px-4 py-2 text-sm">Try again</button>
      </div>
    );
  }

  const { website: site, pages, content } = state.data;
  const homePage = pages.find((p) => p.slug === "home") || pages[0];
  const blocks = (homePage?.blocks || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));

  return (
    <div style={{ ...themeStyle, minHeight: "100vh", background: "var(--fs-bg)", color: "var(--fs-ink)" }}
      data-testid="fs-public-root">
      <FarmerSiteHeader site={site} />
      <main>
        {blocks.length === 0 && (
          <section className="py-24 text-center px-5">
            <Plant size={40} weight="fill" style={{ color: "var(--fs-brand)" }} className="mx-auto" />
            <p className="mt-4 text-lg" style={{ color: "var(--fs-muted)" }}>
              This farm site is still being planted.
            </p>
          </section>
        )}
        {blocks.map((b) => (
          <div key={b.id}>{renderBlock({ block: b, site, content })}</div>
        ))}
        {/* Additional (non-home) pages render as anchor sections. */}
        {pages.filter((p) => p.slug !== homePage?.slug).map((page) => {
          const pageBlocks = (page.blocks || []).slice().sort((a, b) => (a.position || 0) - (b.position || 0));
          return (
            <div key={page.id} id={page.slug}>
              {pageBlocks.map((b) => (
                <div key={b.id}>{renderBlock({ block: b, site, content })}</div>
              ))}
            </div>
          );
        })}
      </main>
      <FarmerSiteFooter site={site} />
    </div>
  );
}
